import React, { useEffect, useState, useMemo, useRef } from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  filterDashboardMarriedAnniversaries,
  getAnniversaryCoupleTrailingName,
  getAnniversaryPersonNameParts,
  isDashboardInLawMember,
  isDashboardUpcomingDeceasedCouple,
  isDashboardUpcomingDeceasedMember,
  normalizeDashboardAnniversariesList,
} from "../../../Utils/dashboardMemberDisplayName";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getAnniversaryPersonPhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import useCentreFilterSection from "../../../Utils/useCentreFilterSection";
import DashboardPeriodPickers, {
  DASHBOARD_PERIOD_ALL_MONTHS as ALL_MONTHS,
  DASHBOARD_PERIOD_MONTH_NAMES,
} from "./DashboardPeriodPickers";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import {
  getCalendarPartsFromDateValue,
  getMemberDeathCalendarParts,
  getMemberDobTimestamp,
  getMemberWeddingCalendarParts,
} from "../../../Utils/family-tree/dateAndTime";
import {
  filterDashboardAnniversariesByMonth,
  getDashboardAnniversaryMonthDay,
} from "../../../Utils/dashboardPeriodMonth";
import { sortDashboardMembersByDeathDate } from "../../../Utils/dashboardFamilyLineNumbers";

/**
 * Footer note under the report, worded and spaced exactly as the client typed
 * it (the double spaces are deliberate — the note renders with pre-wrap).
 */
const ANNIVERSARY_GEMS = [
  [25, "Silver"], [30, "Pearl"], [35, "Coral/Jade"], [40, "Ruby"],
  [45, "Sapphire"], [50, "Gold"], [55, "Emerald"], [60, "Diamond"],
];

// Each year and its gem sit in one span so a wrap never splits them apart.
const ANNIVERSARY_GEMS_NOTE = (
  <>
    {"Starting from 25 up to 60 years here are the associated gems:  "}
    {ANNIVERSARY_GEMS.map(([years, gem], i) => (
      <React.Fragment key={years}>
        {i === 0 ? "" : i === ANNIVERSARY_GEMS.length - 1 ? ", and " : ", "}
        <span className="anniversaries-gems-pair">{`${years}  ${gem}`}</span>
      </React.Fragment>
    ))}
  </>
);

/** Matches & after optional whitespace normalization (Unicode → ASCII handled in sanitize). */
const AMPERSAND_SPLIT_RE = /\s*&\s*/;

function sanitizeAmpersandChars(text) {
  if (text == null || typeof text !== "string") return "";
  return text
    .replace(/\uFF06/g, "&") // FULLWIDTH AMPERSAND
    .replace(/\uFE60/g, "&"); // SMALL AMPERSAND
}

/** Ensure ampersands in a name fragment have spaces (e.g. Yvonne&Basil → Yvonne & Basil). */
function normalizeAmpersandSpacing(text) {
  if (text == null || typeof text !== "string") return "";
  return text
    .split(AMPERSAND_SPLIT_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
    .join(" & ");
}

function rawCoupleStrings(anniversary) {
  const first =
    anniversary?.first_name ??
    anniversary?.firstName ??
    "";
  const spouse =
    anniversary?.spouse_first_name ??
    anniversary?.spouseFirstName ??
    "";
  return {
    firstName: sanitizeAmpersandChars(String(first)).trim(),
    spouseFirstName: sanitizeAmpersandChars(String(spouse)).trim(),
  };
}

/**
 * Split combined couple strings from the API (e.g. first_name "Yvonne&Basil").
 * If first_name contains &, we always split it — some APIs also send spouse_first_name
 * redundantly, which previously blocked splitting.
 */
function getCoupleFirstNames(anniversary) {
  let { firstName, spouseFirstName } = rawCoupleStrings(anniversary);

  if (firstName.includes("&")) {
    const parts = firstName.split(AMPERSAND_SPLIT_RE).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      firstName = parts[0];
      spouseFirstName = parts.slice(1).join(" & ");
    }
  } else if (!firstName && spouseFirstName.includes("&")) {
    const parts = spouseFirstName.split(AMPERSAND_SPLIT_RE).map((p) => p.trim()).filter(Boolean);
    if (parts.length >= 2) {
      firstName = parts[0];
      spouseFirstName = parts.slice(1).join(" & ");
    }
  }

  return {
    firstName: normalizeAmpersandSpacing(firstName),
    spouseFirstName: normalizeAmpersandSpacing(spouseFirstName),
  };
}

/** Compact layout shows two-letter weekdays: Sunday -> SU, Monday -> MO, ... */
const WEEKDAY_ABBREVIATIONS = {
  sunday: "SU",
  monday: "MO",
  tuesday: "TU",
  wednesday: "WE",
  thursday: "TH",
  friday: "FR",
  saturday: "SA",
};

const abbreviateWeekday = (dayOfWeek) => {
  const weekday = String(dayOfWeek || "").trim();
  return (
    WEEKDAY_ABBREVIATIONS[weekday.toLowerCase()] || weekday.slice(0, 2).toUpperCase()
  );
};

const ANNIVERSARY_MONTH_NAMES = DASHBOARD_PERIOD_MONTH_NAMES;

const ANNIVERSARY_WEEKDAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const getAnniversaryMonthDay = getDashboardAnniversaryMonthDay;

/** Day of the wedding, for the date sorts. -1 when unknown. */
const getAnniversaryDayNum = (anniversary) =>
  getAnniversaryMonthDay(anniversary)?.day ?? -1;

/** "7 (Wednesday)" — the wedding day, on the weekday it falls in `year`. */
function formatAnniversaryDayWeekday(anniversary, year) {
  const parts = getAnniversaryMonthDay(anniversary);
  if (!parts) return "";

  const date = new Date(Date.UTC(year, parts.month, parts.day));
  const weekday = isNaN(date.getTime())
    ? ""
    : ANNIVERSARY_WEEKDAY_NAMES[date.getUTCDay()] || "";
  return weekday ? `${parts.day} (${weekday})` : String(parts.day);
}

/**
 * The anniversary count a row prints in its "Year(s)" column, as a number, for
 * the year the list is read against. The payload's own `years` is for the
 * current year, so any other year moves it by the difference; a row without
 * the count (the roster's) takes it from the wedding year. -1 marks a row with
 * neither, so the sort can park it at the end.
 */
const getAnniversaryYearsNum = (anniversary, year = new Date().getFullYear()) => {
  // A marriage that ended with a spouse's death keeps the count it reached.
  if (anniversary?.isDeceasedSpouseCouple) {
    return anniversary.marriedYearsAtDeath ?? -1;
  }

  const years = parseInt(anniversary?.years, 10);
  if (!Number.isNaN(years)) {
    return years + (year - new Date().getFullYear());
  }

  const wedding = getMemberWeddingCalendarParts(anniversary);
  return wedding ? year - wedding.year : -1;
};

/**
 * The word after the count in the years column. A first anniversary reads
 * "1 Yr" / "1 Year", never "1 Yrs"; every other count keeps the plural the
 * column has always shown (compact abbreviates it).
 */
const formatAnniversaryYearsLabel = (years, isCompactLayout) => {
  if (years === 1) return isCompactLayout ? "Yr" : "Year";
  return isCompactLayout ? "Yrs" : "Year(s)";
};

/** The couple a row names, from either side: wedding date + the two names. */
function getCoupleKey(row) {
  const { firstName, spouseFirstName } = getCoupleFirstNames(row);
  const names = [firstName, spouseFirstName]
    .map((name) => name.toLowerCase())
    .sort()
    .join("&");
  const wedding = getMemberWeddingCalendarParts(row);
  const weddingKey = wedding
    ? `${wedding.year}-${wedding.month}-${wedding.day}`
    : "";
  return `${weddingKey}|${names}`;
}

/** Leading word of a first name, lowercased — "Frank \"Butch\"" keys on "frank". */
function leadingNameKey(name) {
  return String(name ?? "")
    .replace(/["“”]/g, " ")
    .trim()
    .split(/\s+/)[0]
    .toLowerCase();
}

/**
 * The roster's in-laws, as "in-law|descendant" first-name pairs. The payload
 * never says which side of a marriage married in, but the roster lists the
 * in-law's own row (marked is_in_laws) naming the descendant they married —
 * in descendants_first_name, or as the row's spouse — so the half of a couple
 * whose pair is here is the in-law who gets the asterisk.
 */
function buildInLawNamesByCouple(rosterMembers) {
  const pairs = new Set();
  rosterMembers.forEach((row) => {
    if (!isDashboardInLawMember(row)) return;
    const inLaw = leadingNameKey(row?.first_name);
    const descendant = leadingNameKey(
      row?.descendants_first_name ?? row?.descendant_first_name ?? row?.spouse_first_name
    );
    if (inLaw && descendant) pairs.add(`${inLaw}|${descendant}`);
  });
  return pairs;
}

function isSpouseInLawFlagged(row) {
  return (
    row?.spouse_is_in_laws === true ||
    row?.spouse_is_in_laws === "true" ||
    row?.spouse_is_in_laws === 1
  );
}

/**
 * Which sides of a couple row carry the in-law asterisk — the row's own flags
 * first, then the roster's in-law rows. Start/Top of Line couples carry none.
 */
function getCoupleInLawSides(row, inLawNamesByCouple) {
  if (isExplicitDashboardStartOrTopOfLineMember(row)) return { primary: false, spouse: false };
  const { firstName, spouseFirstName } = getCoupleFirstNames(row);
  const first = leadingNameKey(firstName);
  const spouse = leadingNameKey(spouseFirstName);
  return {
    primary:
      isDashboardInLawMember(row) ||
      Boolean(first && spouse && inLawNamesByCouple.has(`${first}|${spouse}`)),
    spouse:
      isSpouseInLawFlagged(row) ||
      Boolean(first && spouse && inLawNamesByCouple.has(`${spouse}|${first}`)),
  };
}

/**
 * One row per couple. The roster lists both halves of a marriage — the
 * descendant and the in-law — each naming the other as spouse, so the pair
 * reads the same from either side; the descendant's row is the one kept,
 * which is the side the month list names first too.
 */
function dedupeRosterCouples(rows) {
  const ordered = [...rows].sort(
    (a, b) => Number(isDashboardInLawMember(a)) - Number(isDashboardInLawMember(b))
  );
  const seen = new Set();
  return ordered.filter((row) => {
    const key = getCoupleKey(row);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * A month's rows: the payload's own rows for it first — those are what the
 * dashboard counts, with the year's count and weekday already spelled out —
 * and then any roster couple of that month the payload left out, so a month
 * the endpoint does not cover still reads off the tree.
 */
function mergeMonthAnniversaries(payloadRows, rosterRows) {
  const seenIds = new Set();
  const seenCouples = new Set();
  payloadRows.forEach((row) => {
    if (row?.id != null) seenIds.add(String(row.id));
    seenCouples.add(getCoupleKey(row));
  });

  const extras = rosterRows.filter((row) => {
    if (row?.id != null && seenIds.has(String(row.id))) return false;
    return !seenCouples.has(getCoupleKey(row));
  });

  return [...payloadRows, ...extras];
}

const MARITAL_STATUS_MARRIED = 2;
const MARITAL_STATUS_WIDOWED = 4;

/**
 * A roster row that can name a marriage a death ended: Married, or Widowed —
 * the survivor's record is often moved to Widowed once the spouse passes, and
 * the regular list leaves those out. Rows with no status count when they carry
 * a wedding date, as they do on the regular list.
 */
function isMarriedOrWidowedRow(row) {
  const raw = row?.marital_status;
  if (raw == null || raw === "") return Boolean(getMemberWeddingCalendarParts(row));
  const status = Number(raw);
  return status === MARITAL_STATUS_MARRIED || status === MARITAL_STATUS_WIDOWED;
}

function isSpouseDeceasedOnRow(row) {
  if (Number(row?.spouse_present_or_deceased) === 2) return true;
  if (Number(row?.spouse_present_or_deceased) === 1) return false;
  return Boolean(getSpouseDeathCalendarParts(row));
}

function getSpouseDeathCalendarParts(row) {
  return getCalendarPartsFromDateValue(
    row?.spouse_date_of_death ?? row?.spouse_date_of_passing ?? row?.spouse_dod
  );
}

/**
 * The couples one of whom died while they were still married, keyed by couple.
 * Both halves of a marriage are read — the descendant's row and the in-law's —
 * since each carries its own date of death; the marriage ends with the first
 * one. The count is the year of that death less the wedding year (1921 → 1953
 * is 32 years), so a death a few days short of the anniversary still counts
 * that year, as the family reckons it. Each couple is shown from the
 * descendant's side, flagged with which of the two has passed.
 */
function buildDeceasedSpouseCouples(rows) {
  const halvesByCouple = new Map();
  rows.forEach((row) => {
    if (!getMemberWeddingCalendarParts(row)) return;
    const key = getCoupleKey(row);
    if (!halvesByCouple.has(key)) halvesByCouple.set(key, []);
    halvesByCouple.get(key).push(row);
  });

  const couples = new Map();
  halvesByCouple.forEach((halves, key) => {
    const [display] = [...halves].sort(
      (a, b) => Number(isDashboardInLawMember(a)) - Number(isDashboardInLawMember(b))
    );
    const displayFirst = getCoupleFirstNames(display).firstName.toLowerCase();

    let primaryDeceased = false;
    let spouseDeceased = false;
    const deathYears = [];
    halves.forEach((half) => {
      const isDisplaySide =
        getCoupleFirstNames(half).firstName.toLowerCase() === displayFirst;
      if (isDashboardUpcomingDeceasedMember(half)) {
        if (isDisplaySide) primaryDeceased = true;
        else spouseDeceased = true;
        const death = getMemberDeathCalendarParts(half);
        if (death) deathYears.push(death.year);
      }
      if (isSpouseDeceasedOnRow(half)) {
        if (isDisplaySide) spouseDeceased = true;
        else primaryDeceased = true;
        const death = getSpouseDeathCalendarParts(half);
        if (death) deathYears.push(death.year);
      }
    });
    if (!primaryDeceased && !spouseDeceased) return;

    const wedding = getMemberWeddingCalendarParts(display);
    const years = deathYears.length ? Math.min(...deathYears) - wedding.year : null;
    couples.set(key, {
      ...display,
      isDeceasedSpouseCouple: true,
      marriedYearsAtDeath: years != null && years >= 0 ? years : null,
      primaryDeceased,
      spouseDeceased,
    });
  });
  return couples;
}

/**
 * Payload rows the backend itself flags as a marriage a death ended. They keep
 * the survivor's current status — Widowed (Terry & Mayumi) or Separated —
 * which the married-only filter drops, so they are read in separately.
 */
function getFlaggedDeceasedCoupleRows(anniversaries) {
  return normalizeDashboardAnniversariesList(anniversaries).filter(
    (row) => row?.is_deceased_couple === true || row?.is_deceased_couple === "true"
  );
}

/**
 * The page's two lists from one statistics payload: the marriages still going
 * (as the payload and the roster name them) and the ones a death ended. The
 * regular list shows only living couples; the Spouse(s) (Widowed/Deceased) filter shows
 * only the latter.
 */
function splitAnniversaryCouples(payloadRows, rosterMembers, flaggedDeceasedRows = []) {
  const deceasedCouples = buildDeceasedSpouseCouples([
    ...payloadRows,
    ...flaggedDeceasedRows,
    ...rosterMembers.filter(isMarriedOrWidowedRow),
  ]);
  const isOngoing = (row) =>
    !deceasedCouples.has(getCoupleKey(row)) && !isDashboardUpcomingDeceasedCouple(row);

  return {
    payloadRows: payloadRows.filter(isOngoing),
    rosterCouples: dedupeRosterCouples(
      filterDashboardMarriedAnniversaries(rosterMembers)
    ).filter(isOngoing),
    deceasedCouples: [...deceasedCouples.values()],
  };
}

/**
 * A month's rows on the page: the couples of that month with both spouses
 * living — or, with the Spouse(s) (Widowed/Deceased) filter on, only the marriages a
 * death ended (an angel after each spouse who passed).
 */
function getMonthAnniversaryRows(couples, monthIndex, deceasedOnly = false) {
  if (deceasedOnly) {
    return filterDashboardAnniversariesByMonth(couples.deceasedCouples, monthIndex);
  }
  return mergeMonthAnniversaries(
    filterDashboardAnniversariesByMonth(couples.payloadRows, monthIndex),
    filterDashboardAnniversariesByMonth(couples.rosterCouples, monthIndex)
  );
}

/**
 * How many couples the whole-year list shows, from a statistics payload — the
 * dashboard card's count, built from the same month-by-month rows the page
 * opens on so the card and the page carry one total.
 */
export function countDashboardYearAnniversaries(statisticsData) {
  const anniversariesData =
    statisticsData?.anniversaries_this_month?.details ||
    statisticsData?.anniversaries_this_month ||
    [];
  const couples = splitAnniversaryCouples(
    filterDashboardMarriedAnniversaries(anniversariesData),
    extractAllMembersFromStatisticsResponse(statisticsData),
    getFlaggedDeceasedCoupleRows(anniversariesData)
  );
  return ANNIVERSARY_MONTH_NAMES.reduce(
    (total, _, monthIndex) => total + getMonthAnniversaryRows(couples, monthIndex).length,
    0
  );
}

function AnniversariesThisWeek() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  // The full roster: the colour legend is built from it, and so is every month
  // other than the current one — see periodMonths.
  const [rosterMembers, setRosterMembers] = useState([]);
  // The payload's own anniversaries_this_month rows — every month it names,
  // not only the current one; see periodMonths.
  const [payloadAnniversaries, setPayloadAnniversaries] = useState([]);
  const [flaggedDeceasedRows, setFlaggedDeceasedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(now.getFullYear());
  // 1–12, or ALL_MONTHS for the whole year — the default.
  const [month, setMonth] = useState(ALL_MONTHS);
  const [nameColumnWidth, setNameColumnWidth] = useState("auto");
  const [dateColumnWidth, setDateColumnWidth] = useState("auto");
  const [yearsColumnWidth, setYearsColumnWidth] = useState("auto");
  const [yearsNumColumnWidth, setYearsNumColumnWidth] = useState("2ch");
  // The label track is sized to the plural ("Yrs" / "Year(s)") on every row, so
  // a "1 Yr" row does not narrow its years column and pull the name column left.
  const [yearsLabelColumnWidth, setYearsLabelColumnWidth] = useState("auto");
  const [headOfLineColumnWidth, setHeadOfLineColumnWidth] = useState("auto");
  // Spouse(s) (Widowed/Deceased): only the marriages a spouse's death ended.
  const [showDeceasedSpouses, setShowDeceasedSpouses] = useState(false);
  const [sortBy, setSortBy] = useState("date-asc"); // "none", "alpha-asc", "alpha-desc", "color", "date-asc", "date-desc", "year-asc", "year-desc", "oldest", "youngest", "death-asc"
  const [isLargeView, setIsLargeView] = useState(false);
  const [weekdayColumnWidth, setWeekdayColumnWidth] = useState("auto");
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const listRef = useRef(null);
  // No search box here to centre the filter bar, so the heading line does.
  const headerLeftRef = useRef(null);
  useCentreFilterSection(headerLeftRef, true, false, listRef);

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  // Fetch anniversaries from API
  useEffect(() => {
    const fetchAnniversaries = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setPayloadAnniversaries([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          setRosterMembers(extractAllMembersFromStatisticsResponse(response.data));
          // Extract anniversaries from the response
          // The API returns: data.anniversaries_this_month.details (array)
          const anniversariesData = 
            response?.data?.anniversaries_this_month?.details || 
            response?.data?.anniversaries_this_month ||
            [];
          
          setPayloadAnniversaries(
            filterDashboardMarriedAnniversaries(anniversariesData)
          );
          setFlaggedDeceasedRows(getFlaggedDeceasedCoupleRows(anniversariesData));
        } else {
          setPayloadAnniversaries([]);
        }
      } catch (error) {
        console.error("Error fetching anniversaries:", error);
        setPayloadAnniversaries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnniversaries();
  }, []);

  // The months the pickers ask for, each with its rows. Unlike the birthday
  // list, the payload's anniversaries list is not confined to the current
  // month — a November couple comes back in September's payload — so every
  // month, the current one included, is its own rows out of that list by the
  // month each is dated in, topped up from the roster (the married rows, one
  // per couple) for any couple of that month the payload left out. The
  // payload's rows carry the current year's count and weekday already spelled
  // out, and both are recomputed for the year picked — so the rows print the
  // same either way.
  // Couples a spouse's death parted are left out of the regular list; the
  // Spouse(s) (Widowed/Deceased) filter lists just those, their count frozen at the year
  // of that death.
  const inLawNamesByCouple = useMemo(
    () => buildInLawNamesByCouple(rosterMembers),
    [rosterMembers]
  );
  const couples = useMemo(
    () => splitAnniversaryCouples(payloadAnniversaries, rosterMembers, flaggedDeceasedRows),
    [payloadAnniversaries, rosterMembers, flaggedDeceasedRows]
  );
  const periodMonths = useMemo(() => {
    const monthIndexes =
      month === ALL_MONTHS
        ? ANNIVERSARY_MONTH_NAMES.map((_, index) => index)
        : [month - 1];

    return monthIndexes.map((monthIndex) => ({
      monthIndex,
      monthName: ANNIVERSARY_MONTH_NAMES[monthIndex],
      rows: getMonthAnniversaryRows(couples, monthIndex, showDeceasedSpouses),
    }));
  }, [month, couples, showDeceasedSpouses]);

  const anniversaries = useMemo(
    () => periodMonths.flatMap((group) => group.rows),
    [periodMonths]
  );

  // Helper function to format date by removing month name (since all are in the same month)
  const formatDateWithoutMonth = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      return "";
    }
    
    // Remove month name from the beginning (e.g., "January 7 (Wednesday) - 29 Years" -> "7 (Wednesday) - 29 Years")
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                       'July', 'August', 'September', 'October', 'November', 'December'];
    
    let formatted = dateString.trim();
    
    // Remove month name if present
    for (const month of monthNames) {
      if (formatted.startsWith(month + ' ')) {
        formatted = formatted.substring(month.length + 1).trim();
        break;
      }
    }
    
    // Keep day of week in parentheses - don't remove it
    
    return formatted;
  };

  // Split formatted date into parts to align numbers vertically
  const splitDateParts = (dateDisplay) => {
    if (!dateDisplay || typeof dateDisplay !== "string") {
      return { dayOfWeek: "", dayNum: "", trailingText: "" };
    }
    // Pattern: "(Wednesday) 7 - 29 Years"
    const matchWeekdayFirst = dateDisplay.match(/^\(([^)]+)\)\s*(\d{1,2})(.*)$/);
    if (matchWeekdayFirst) {
      return {
        dayOfWeek: matchWeekdayFirst[1],
        dayNum: matchWeekdayFirst[2],
        trailingText: (matchWeekdayFirst[3] || "").trimStart(),
      };
    }
    // Pattern: "7 (Wednesday) - 29 Years"
    const matchDayFirst = dateDisplay.match(/^(\d{1,2})\s*\(([^)]+)\)(.*)$/);
    if (matchDayFirst) {
      return {
        dayOfWeek: matchDayFirst[2],
        dayNum: matchDayFirst[1],
        trailingText: (matchDayFirst[3] || "").trimStart(),
      };
    }
    return { dayOfWeek: "", dayNum: "", trailingText: "" };
  };

  // Helper function to render member row with 4 columns
  const renderMemberRow = (anniversary) => {
    const { firstName, spouseFirstName } = getCoupleFirstNames(anniversary);
    const suffix = anniversary?.suffix || "";
    const specialName = anniversary?.special_name || "";
    const spouseSuffix = anniversary?.spouse_special_name || anniversary?.spouse_suffix || "";
    const spouseSpecialName = anniversary?.spouse_special_name || "";
    
    const hasSpouseSide = Boolean(
      (spouseFirstName || "").trim() || spouseSuffix || spouseSpecialName
    );
    // First person: first name only — the surname sits on the spouse side and the
    // suffix / special initial is rendered at the end of the couple string.
    const primaryNameParts = getAnniversaryPersonNameParts(anniversary, {
      firstName,
      suffix,
      specialName,
      side: "primary",
      hasSpouseSide,
    });
    const spouseNameParts = getAnniversaryPersonNameParts(anniversary, {
      firstName: spouseFirstName,
      suffix: spouseSuffix,
      specialName: spouseSpecialName,
      side: "spouse",
      hasSpouseSide,
    });
    const coupleTrailingName = getAnniversaryCoupleTrailingName(suffix, specialName);
    const inLawSides = getCoupleInLawSides(anniversary, inLawNamesByCouple);
    const showPrimaryInLawAsterisk = inLawSides.primary;
    const showSpouseInLawAsterisk = inLawSides.spouse;
    // Multiple-birth markers are not shown on wedding anniversary rows.
    const showSpouseJoiner = hasSpouseSide;

    // A marriage a death ended tags each spouse who has passed after that
    // spouse's own name; other rows keep the single angel at the end.
    const isDeceasedSpouseCouple = Boolean(anniversary?.isDeceasedSpouseCouple);
    const isDeceased = !isDeceasedSpouseCouple && anniversary?.present_or_deceased === 2;
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(anniversary, headsMap);
    const hexCode = anniversary?.hex_code || "#000000";
    const colorName = anniversary?.color_name || "";

    // The wedding day, on the weekday it falls in the year picked. The payload's
    // own text is only the fallback for a row whose date cannot be read.
    let dateDisplay = formatAnniversaryDayWeekday(anniversary, year);
    if (!dateDisplay) {
      dateDisplay = anniversary?.date || "";
      // Remove "Xth Anniversary" text if present (e.g., "13th Anniversary")
      dateDisplay = dateDisplay.replace(/\s*-\s*\d+(st|nd|rd|th)\s+Anniversary/gi, '').trim();
      // Remove month name from date display (but keep years separate)
      dateDisplay = formatDateWithoutMonth(dateDisplay);
      // Remove years from dateDisplay since it will be in a separate column
      dateDisplay = dateDisplay.replace(/\s*\d+\s+Years?/gi, '').trim();
    }
    const yearsNum = getAnniversaryYearsNum(anniversary, year);
    const years = yearsNum === -1 ? 0 : yearsNum;

    // Calculate sizes based on large view mode
    const iconSize = isLargeView ? '18px' : '12px';
    const angel = (
      <span className={`dashboard-detail-angel-icon ${isLargeView ? 'large-view' : ''}`}>
        <img
          src={window.getPublicUrl("/assets/images/angel.png")}
          alt="deceased"
          style={{ width: iconSize, height: iconSize }}
        />
      </span>
    );

    return (
      <div className={`dashboard-detail-row ${isLargeView ? 'large-view' : ''}`}>
        {/* Column 1: Date (day + weekday) */}
        <div
          className={`dashboard-detail-date-col ${isLargeView ? 'large-view' : ''}`}
          style={{
            // Compact: the grid tracks already line every row up, so the column can
            // shrink to its content and sit close to the years column.
            // Compact spacing comes from the row's flex gap, not per-column margins
            width: isCompactLayout ? 'auto' : dateColumnWidth,
            // Two children, so compact needs exactly two tracks — a third track
            // squeezed the weekday into 0.25ch and let it overflow into the
            // years column. The 0.5ch column-gap is the space after the day.
            // The weekday track is the measured width of the widest abbreviation,
            // so a "(WE)" row does not push its name further right than a "(SA)" row.
            gridTemplateColumns: isCompactLayout
              ? `2ch ${weekdayColumnWidth}`
              : `2ch 1ch ${weekdayColumnWidth}`,
            alignItems: 'center',
          }}
        >
          {(() => {
            const { dayOfWeek, dayNum } = splitDateParts(dateDisplay);
            if (!dayOfWeek) {
              // Fallback if parsing fails
              return <span>{dateDisplay}</span>;
            }
            return (
              <>
                <span className="dashboard-detail-date-day">{dayNum}</span>
                <span
                  className="dashboard-detail-date-weekday"
                  style={{ width: isCompactLayout ? 'auto' : weekdayColumnWidth }}
                >
                  ({isCompactLayout ? abbreviateWeekday(dayOfWeek) : dayOfWeek})
                </span>
              </>
            );
          })()}
        </div>
        
        {/* Column 2: Years */}
        <div
          className={`dashboard-detail-years-col ${isLargeView ? 'large-view' : ''}`}
          style={{
            width: isCompactLayout ? 'auto' : yearsColumnWidth,
            gridTemplateColumns: `${yearsNumColumnWidth} ${yearsLabelColumnWidth}`,
          }}
        >
          <span className="dashboard-detail-years-num">{years}</span>
          <span className="dashboard-detail-years-label">
            {formatAnniversaryYearsLabel(years, isCompactLayout)}
          </span>
        </div>

        {/* Column 3: Couple Name */}
        {/* Compact takes the measured width as a floor rather than a fixed size:
            it lines the colour names up in their own column without ever clipping
            a couple whose name measures wider than the rest. */}
        <div
          className={`dashboard-detail-name-col ${isLargeView ? 'large-view' : ''}`}
          style={
            isCompactLayout
              ? { width: 'auto', minWidth: nameColumnWidth }
              : { width: nameColumnWidth }
          }
        >
        <DashboardNameMarkerGroup>
        <span className="dashboard-detail-couple-wrap">
          <DashboardPhotoName
            {...getAnniversaryPersonPhotoUrls(anniversary)}
            onViewPhoto={openPhoto}
          >
            <DashboardMemberNameLabel
              baseName={primaryNameParts.baseName}
              descendantSpouseName={primaryNameParts.descendantSpouseName}
              afterBaseName={
                showPrimaryInLawAsterisk && primaryNameParts.descendantSpouseName ? (
                  <DashboardInLawAsterisk isLargeView={isLargeView} />
                ) : null
              }
            />
          </DashboardPhotoName>
          {showPrimaryInLawAsterisk && !primaryNameParts.descendantSpouseName && (
            <DashboardInLawAsterisk isLargeView={isLargeView} />
          )}
          {isDeceasedSpouseCouple && anniversary.primaryDeceased && angel}
          {showSpouseJoiner ? (
            <>
              <span className="dashboard-detail-couple-joiner">&</span>
              <DashboardPhotoName
                {...getAnniversaryPersonPhotoUrls(anniversary)}
                onViewPhoto={openPhoto}
              >
                <DashboardMemberNameLabel
                  baseName={spouseNameParts.baseName}
                  descendantSpouseName={spouseNameParts.descendantSpouseName}
                />
              </DashboardPhotoName>
            </>
          ) : null}
          {coupleTrailingName && (
            <span
              className="dashboard-detail-couple-trailing"
            >
              {coupleTrailingName}
            </span>
          )}
          {/* The spouse's markers close the name, after the couple's suffix
              ("Lisa Witherspoon, Jr.*"), never between surname and suffix. */}
          {showSpouseJoiner && showSpouseInLawAsterisk && (
            <DashboardInLawAsterisk isLargeView={isLargeView} />
          )}
          {showSpouseJoiner && isDeceasedSpouseCouple && anniversary.spouseDeceased && angel}
        </span>
        {isDeceased && angel}
        </DashboardNameMarkerGroup>
        </div>
        
        {/* Column 4: Head of Line Name and Color — compact prints the line colour in
            its own colour, and the footer legend ties each colour to its head */}
        {isCompactLayout ? (
          <div className="dashboard-detail-head-col dashboard-detail-line-col--compact">
            <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
          </div>
        ) : (
          <div className={`dashboard-detail-head-col ${isLargeView ? 'large-view' : ''}`} style={{ width: headOfLineColumnWidth }}>
            <div className={isLargeView ? 'large-view-text' : ''}>
              <DashboardPhotoHeadLine
                name={headOfLineName || "Start/Top of Line"}
                hexCode={hexCode}
                colorName={colorName ? `${colorName} Line` : ""}
                imageUrl={getDashboardMemberPhotoUrl(anniversary, "head_image")}
                onViewPhoto={openPhoto}
                member={anniversary}
                headsMap={headsMap}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // Sort anniversaries based on sortBy. The month grouping is the period's own
  // shape and stands under every sort; only the rows inside a month re-order.
  // Date descending also reads the months last to first, so a whole-year list
  // runs December back to January.
  const sortedMonths = useMemo(() => {
    const sortAnniversaryRows = (rows) => {
    if (!rows.length || sortBy === "none") return rows;

    const sorted = [...rows];

    if (sortBy === "death-asc") {
      return sortDashboardMembersByDeathDate(sorted, {
        appendDescendantSpouse: false,
      });
    }

    if (sortBy === "oldest") {
      return sorted.sort((a, b) => {
        const dobA = getMemberDobTimestamp(a);
        const dobB = getMemberDobTimestamp(b);
        if (dobA === -1 && dobB === -1) return 0;
        if (dobA === -1) return 1;
        if (dobB === -1) return -1;
        return dobA - dobB;
      });
    }

    if (sortBy === "youngest") {
      return sorted.sort((a, b) => {
        const dobA = getMemberDobTimestamp(a);
        const dobB = getMemberDobTimestamp(b);
        if (dobA === -1 && dobB === -1) return 0;
        if (dobA === -1) return 1;
        if (dobB === -1) return -1;
        return dobB - dobA;
      });
    }
    
    if (sortBy === "year-asc" || sortBy === "year-desc") {
      // The "Year(s)" column each row prints — same column In Memoriam sorts by
      // its Year button. Couples on the same count fall back to the couple name
      // so the order is stable.
      const descending = sortBy === "year-desc";
      return sorted.sort((a, b) => {
        const yearsA = getAnniversaryYearsNum(a, year);
        const yearsB = getAnniversaryYearsNum(b, year);
        if (yearsA === -1 && yearsB === -1) {
          /* fall through to the name comparison */
        } else if (yearsA === -1) return 1;
        else if (yearsB === -1) return -1;
        else if (yearsA !== yearsB) {
          return descending ? yearsB - yearsA : yearsA - yearsB;
        }

        const { firstName, spouseFirstName } = getCoupleFirstNames(a);
        const { firstName: firstNameB, spouseFirstName: spouseFirstNameB } =
          getCoupleFirstNames(b);
        const nameA = `${firstName} & ${spouseFirstName}`.toLowerCase();
        const nameB = `${firstNameB} & ${spouseFirstNameB}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    if (sortBy === "alpha-asc") {
      return sorted.sort((a, b) => {
        const { firstName, spouseFirstName } = getCoupleFirstNames(a);
        const { firstName: firstNameB, spouseFirstName: spouseFirstNameB } =
          getCoupleFirstNames(b);
        const nameA = `${firstName} & ${spouseFirstName}`.toLowerCase();
        const nameB = `${firstNameB} & ${spouseFirstNameB}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === "alpha-desc") {
      return sorted.sort((a, b) => {
        const { firstName, spouseFirstName } = getCoupleFirstNames(a);
        const { firstName: firstNameB, spouseFirstName: spouseFirstNameB } =
          getCoupleFirstNames(b);
        const nameA = `${firstName} & ${spouseFirstName}`.toLowerCase();
        const nameB = `${firstNameB} & ${spouseFirstNameB}`.toLowerCase();
        return nameB.localeCompare(nameA);
      });
    } else if (sortBy === "color") {
      return sorted.sort((a, b) => {
        const colorA = (a?.color_name || "").toLowerCase();
        const colorB = (b?.color_name || "").toLowerCase();
        if (colorA !== colorB) {
          return colorA.localeCompare(colorB);
        }
        // Same line/color: A–Z by couple name, then calendar day for namesakes
        const { firstName, spouseFirstName } = getCoupleFirstNames(a);
        const { firstName: firstNameB, spouseFirstName: spouseFirstNameB } =
          getCoupleFirstNames(b);
        const nameA = `${firstName} & ${spouseFirstName}`.toLowerCase();
        const nameB = `${firstNameB} & ${spouseFirstNameB}`.toLowerCase();
        const nameCompare = nameA.localeCompare(nameB);
        if (nameCompare !== 0) return nameCompare;

        const dayA = getAnniversaryDayNum(a);
        const dayB = getAnniversaryDayNum(b);
        if (dayA === dayB) return 0;
        if (dayA === -1) return 1;
        if (dayB === -1) return -1;
        return dayA - dayB;
      });
    } else if (sortBy === "date-asc") {
      return sorted.sort((a, b) => {
        const dayA = getAnniversaryDayNum(a);
        const dayB = getAnniversaryDayNum(b);
        
        if (dayA === -1 && dayB === -1) return 0;
        if (dayA === -1) return 1;
        if (dayB === -1) return -1;
        return dayA - dayB;
      });
    } else if (sortBy === "date-desc") {
      return sorted.sort((a, b) => {
        const dayA = getAnniversaryDayNum(a);
        const dayB = getAnniversaryDayNum(b);
        
        if (dayA === -1 && dayB === -1) return 0;
        if (dayA === -1) return 1;
        if (dayB === -1) return -1;
        return dayB - dayA;
      });
    }
    
    return sorted;
    };

    const months = periodMonths
      .filter((group) => group.rows.length > 0)
      .map((group) => ({ ...group, rows: sortAnniversaryRows(group.rows) }));

    return sortBy === "date-desc" ? months.reverse() : months;
  }, [periodMonths, sortBy, year]);

  const sortedAnniversaries = useMemo(
    () => sortedMonths.flatMap((group) => group.rows),
    [sortedMonths]
  );

  const totalCount = anniversaries.length;

  // The whole year (the default) reads "for the Year"; a single month picked
  // from the dropdown drops it, since the list no longer covers the year.
  const listTitle =
    month === ALL_MONTHS
      ? "Wedding Anniv for the Year"
      : "Wedding Anniv";

  // Measure the actual width of name and head of line elements after render
  useEffect(() => {
    if (sortedAnniversaries.length === 0 || loading) return;
    
    // Helper functions (local copies for useEffect)
    const getHeadOfLineNameLocal = (row) =>
      getDashboardFamilyLineHeadDisplayName(row, headsMap);
    
    // Use appropriate font sizes based on view mode — desktop rows are 18px
    // (one size up for older readers), phone rows stay 16px; see App.css.
    const fontSize = isLargeView ? '24px' : isCompactLayout ? '16px' : '18px';
    const dateFontSize = isLargeView ? '24px' : isCompactLayout ? '14px' : '18px';
    const iconSize = isLargeView ? 18 : 12;
    
    // Create temporary elements for measurement
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.visibility = 'hidden';
    tempDiv.style.whiteSpace = 'nowrap';
    tempDiv.style.fontSize = fontSize;
    tempDiv.style.fontFamily = 'inherit';
    document.body.appendChild(tempDiv);
    
    const chMeasure = document.createElement('div');
    chMeasure.style.position = 'absolute';
    chMeasure.style.visibility = 'hidden';
    chMeasure.style.fontSize = fontSize;
    chMeasure.style.fontFamily = 'inherit';
    chMeasure.textContent = '0';
    document.body.appendChild(chMeasure);
    const chWidth = chMeasure.getBoundingClientRect().width;

    // Create a measure for date font size
    const dateMeasure = document.createElement('div');
    dateMeasure.style.position = 'absolute';
    dateMeasure.style.visibility = 'hidden';
    dateMeasure.style.fontSize = dateFontSize;
    dateMeasure.style.fontFamily = 'inherit';
    document.body.appendChild(dateMeasure);
    
    let maxNameWidth = 0;
    let maxDateWidth = 0;
    let maxYearsNumWidth = 0;
    let yearsLabelWidth = 0;
    let maxHeadOfLineWidth = 0;
    let maxWeekdayWidth = 0;
    
    sortedAnniversaries.forEach((anniversary) => {
      const { firstName, spouseFirstName } = getCoupleFirstNames(anniversary);
      const suffix = anniversary?.suffix || "";
      const specialName = anniversary?.special_name || "";
      const spouseSuffix = anniversary?.spouse_special_name || anniversary?.spouse_suffix || "";
      const spouseSpecialName = anniversary?.spouse_special_name || "";
      
      const hasSpouseSide = Boolean(
        (spouseFirstName || "").trim() || spouseSuffix || spouseSpecialName
      );
      const primaryParts = getAnniversaryPersonNameParts(anniversary, {
        firstName,
        suffix,
        specialName,
        side: "primary",
        hasSpouseSide,
      });
      const spouseParts = getAnniversaryPersonNameParts(anniversary, {
        firstName: spouseFirstName,
        suffix: spouseSuffix,
        specialName: spouseSpecialName,
        side: "spouse",
        hasSpouseSide,
      });
      const coupleTrailingName = getAnniversaryCoupleTrailingName(suffix, specialName);
      const isDeceasedSpouseCouple = Boolean(anniversary?.isDeceasedSpouseCouple);
      const isDeceased = !isDeceasedSpouseCouple && anniversary?.present_or_deceased === 2;
      const showJoinerMeasure = hasSpouseSide;

      tempDiv.innerHTML = "";
      const wrap = document.createElement("span");
      wrap.style.display = "inline-flex";
      wrap.style.alignItems = "baseline";
      wrap.style.flexWrap = "nowrap";
      wrap.style.gap = "0";
      tempDiv.appendChild(wrap);

      // The in-law asterisks render bold after each side of the couple, so the
      // measured name has to carry them or the column comes up short of the row.
      const appendAsterisk = () => {
        const ast = document.createElement("span");
        ast.textContent = "*";
        ast.style.fontWeight = "700";
        wrap.appendChild(ast);
      };
      const inLawSidesMeasure = getCoupleInLawSides(anniversary, inLawNamesByCouple);
      const showPrimaryAsteriskMeasure = inLawSidesMeasure.primary;
      const showSpouseAsteriskMeasure = inLawSidesMeasure.spouse;

      // Mirrors DashboardMemberNameLabel — the "(spouse)" half renders bold, so
      // measuring the side as one regular-weight string came up short of the row.
      const appendSide = ({ baseName, descendantSpouseName }) => {
        const base = document.createElement("span");
        base.textContent = baseName;
        wrap.appendChild(base);
        if (!descendantSpouseName) return;
        const open = document.createElement("span");
        open.textContent = " (";
        wrap.appendChild(open);
        const spouse = document.createElement("strong");
        spouse.style.fontWeight = "700";
        spouse.textContent = descendantSpouseName;
        wrap.appendChild(spouse);
        const close = document.createElement("span");
        close.textContent = ")";
        wrap.appendChild(close);
      };

      // An angel after a deceased spouse's own name (Spouse(s) (Widowed/Deceased) rows).
      const appendAngel = () => {
        const iconWrap = document.createElement("span");
        iconWrap.className = "dashboard-detail-angel-icon";
        iconWrap.style.display = "inline-block";
        iconWrap.style.width = `${iconSize}px`;
        iconWrap.style.height = `${iconSize}px`;
        wrap.appendChild(iconWrap);
      };

      appendSide(primaryParts);
      if (showPrimaryAsteriskMeasure) appendAsterisk();
      if (isDeceasedSpouseCouple && anniversary.primaryDeceased) appendAngel();
      if (showJoinerMeasure) {
        const amp = document.createElement("span");
        amp.textContent = "&";
        amp.className = "dashboard-detail-couple-joiner";
        wrap.appendChild(amp);
        appendSide(spouseParts);
      }
      if (coupleTrailingName) {
        const trailingEl = document.createElement("span");
        trailingEl.className = "dashboard-detail-couple-trailing";
        trailingEl.style.whiteSpace = "pre";
        trailingEl.textContent = coupleTrailingName;
        wrap.appendChild(trailingEl);
      }
      if (showJoinerMeasure) {
        if (showSpouseAsteriskMeasure) appendAsterisk();
        if (isDeceasedSpouseCouple && anniversary.spouseDeceased) appendAngel();
      }
      if (isDeceased) {
        const iconWrap = document.createElement("span");
        iconWrap.style.display = "inline-block";
        iconWrap.style.width = `${iconSize}px`;
        iconWrap.style.height = `${iconSize}px`;
        iconWrap.style.verticalAlign = "middle";
        tempDiv.appendChild(iconWrap);
      }
      const nameWidth = tempDiv.getBoundingClientRect().width;
      if (nameWidth > maxNameWidth) {
        maxNameWidth = nameWidth;
      }
      
      // Measure years number width (label "Years" is measured once after the loop).
      const yearsNum = getAnniversaryYearsNum(anniversary, year);
      const years = yearsNum === -1 ? 0 : yearsNum;
      dateMeasure.style.fontSize = fontSize;
      dateMeasure.style.fontWeight = isCompactLayout ? '700' : '400';
      dateMeasure.textContent = String(years);
      const yearsNumWidth = dateMeasure.getBoundingClientRect().width;
      if (yearsNumWidth > maxYearsNumWidth) {
        maxYearsNumWidth = yearsNumWidth;
      }
      dateMeasure.style.fontSize = dateFontSize;
      
      // Measure date width (without month name and years) — what the row prints
      let dateDisplay =
        formatAnniversaryDayWeekday(anniversary, year) || anniversary?.date || "";
      // Remove "Xth Anniversary" text if present
      if (dateDisplay && typeof dateDisplay === 'string') {
        dateDisplay = dateDisplay.replace(/\s*-\s*\d+(st|nd|rd|th)\s+Anniversary/gi, '').trim();
      }
      
      // Remove month name for measurement (keep day of week, remove years)
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      let formattedDate = dateDisplay.trim();
      for (const month of monthNames) {
        if (formattedDate.startsWith(month + ' ')) {
          formattedDate = formattedDate.substring(month.length + 1).trim();
          break;
        }
      }
      // Remove years from date for measurement
      formattedDate = formattedDate.replace(/\s*\d+\s+Years?/gi, '').trim();
      
      // Capture weekday width at the weight the date column actually renders with
      dateMeasure.style.fontWeight = '400';
      const { dayOfWeek } = splitDateParts(formattedDate);
      if (dayOfWeek) {
        dateMeasure.textContent = `(${
          isCompactLayout ? abbreviateWeekday(dayOfWeek) : dayOfWeek
        })`;
        const weekdayWidth = dateMeasure.getBoundingClientRect().width;
        if (weekdayWidth > maxWeekdayWidth) {
          maxWeekdayWidth = weekdayWidth;
        }
      }
      // Measure what is actually rendered — compact layout abbreviates the weekday
      dateMeasure.textContent =
        isCompactLayout && dayOfWeek
          ? formattedDate.replace(`(${dayOfWeek})`, `(${abbreviateWeekday(dayOfWeek)})`)
          : formattedDate;
      const dateWidth = dateMeasure.getBoundingClientRect().width;
      if (dateWidth > maxDateWidth) {
        maxDateWidth = dateWidth;
      }
      
      // Measure head of line width
      const headOfLineName = getHeadOfLineNameLocal(anniversary) || "Start/Top of Line";
      const colorName = anniversary?.color_name || "";
      const colorText = colorName ? ` (●  ${colorName} Line)` : "";
      const headOfLineText = `${headOfLineName}${colorText}`;
      tempDiv.textContent = headOfLineText;
      const headOfLineWidth = tempDiv.getBoundingClientRect().width;
      if (headOfLineWidth > maxHeadOfLineWidth) {
        maxHeadOfLineWidth = headOfLineWidth;
      }
    });
    
    dateMeasure.style.fontSize = fontSize;
    dateMeasure.style.fontWeight = isCompactLayout ? '700' : '400';
    // The plural is the wider label, so the column is sized to it even when
    // every row on the page happens to read "1 Yr".
    dateMeasure.textContent = formatAnniversaryYearsLabel(2, isCompactLayout);
    yearsLabelWidth = dateMeasure.getBoundingClientRect().width;

    document.body.removeChild(tempDiv);
    document.body.removeChild(chMeasure);
    document.body.removeChild(dateMeasure);
    
    const nameWidthInCh = Math.ceil(maxNameWidth / chWidth);
    const dateWidthInCh = Math.ceil(maxDateWidth / chWidth) + (isLargeView ? 2 : 1); // spacing after longest date (bold date text)
    const yearsNumWidthInCh = Math.ceil(maxYearsNumWidth / chWidth);
    const yearsTotalWidth =
      maxYearsNumWidth + 0.5 * chWidth + yearsLabelWidth;
    const yearsWidthInCh = Math.ceil(yearsTotalWidth / chWidth) + 1;
    const headOfLineWidthInCh = Math.ceil(maxHeadOfLineWidth / chWidth);
    // Keep weekday compact; compact layout shows two-letter weekdays, so the floor drops
    const weekdayWidthInCh = Math.max(
      Math.ceil(maxWeekdayWidth / chWidth) - 1,
      isCompactLayout ? 4 : 6
    );
    // Desktop and phone size every column to exactly its longest entry, so the
    // 3ch gap is the whole gap after that entry. The ch-rounded widths above
    // still drive large view.
    const isDesktopLayout = !isLargeView;
    // The date column is a grid: 2ch day number + 0.5ch column gap + the weekday,
    // which overflows its track when the track was trimmed by a ch.
    const dateContentPx =
      2.5 * chWidth + Math.max(maxWeekdayWidth, weekdayWidthInCh * chWidth);
    // Years column: right-aligned number track + 0.5ch column gap + "Year(s)".
    const yearsContentPx =
      yearsNumWidthInCh * chWidth + 0.5 * chWidth + yearsLabelWidth;

    setNameColumnWidth(
      isDesktopLayout ? `${Math.ceil(maxNameWidth)}px` : `${nameWidthInCh}ch`
    );
    setDateColumnWidth(
      isDesktopLayout ? `${Math.ceil(dateContentPx)}px` : `${dateWidthInCh}ch`
    );
    setYearsNumColumnWidth(`${yearsNumWidthInCh}ch`);
    setYearsLabelColumnWidth(`${Math.ceil(yearsLabelWidth)}px`);
    setYearsColumnWidth(
      isDesktopLayout ? `${Math.ceil(yearsContentPx)}px` : `${yearsWidthInCh}ch`
    );
    setHeadOfLineColumnWidth(`${headOfLineWidthInCh}ch`);
    // Compact keeps the exact measured width: the ch-rounded value is trimmed by a
    // ch and would clip the widest abbreviation, while `auto` let it vary per row.
    setWeekdayColumnWidth(
      isCompactLayout ? `${Math.ceil(maxWeekdayWidth)}px` : `${weekdayWidthInCh}ch`
    );
  }, [sortedAnniversaries, loading, isLargeView, isCompactLayout, year, inLawNamesByCouple]);

  // Compact rows are wider than the phone, so the list scrolls sideways. Sorting by
  // line colour is about reading the colours, and those sit at the far right — park
  // the scroll at the start of the name column so the couple and its colour are the
  // pair on screen (clamped, so a row wider than the card still ends at the colour).
  // Any other sort goes back to the left edge, where the date column lives.
  useEffect(() => {
    const list = listRef.current;
    // Desktop lays every column out inside the card, so it never scrolls sideways.
    if (!list || loading || !isCompactLayout) return;

    const frame = requestAnimationFrame(() => {
      if (sortBy !== "color") {
        list.scrollLeft = 0;
        return;
      }
      const nameCol = list.querySelector(
        ".dashboard-detail-row:not(.dashboard-detail-header-row) .dashboard-detail-name-col"
      );
      if (!nameCol) return;
      const nameOffset =
        nameCol.getBoundingClientRect().left -
        list.getBoundingClientRect().left +
        list.scrollLeft;
      list.scrollLeft = Math.max(
        0,
        Math.min(nameOffset, list.scrollWidth - list.clientWidth)
      );
    });

    return () => cancelAnimationFrame(frame);
  }, [
    sortBy,
    isCompactLayout,
    loading,
    sortedAnniversaries,
    nameColumnWidth,
    yearsNumColumnWidth,
  ]);

  // Built from the whole tree, not this page's rows, so the footer legend lists the
  // same family lines here as on every other dashboard list. Built from the rows it
  // only showed lines this page happened to contain.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(rosterMembers, headsMap),
    [rosterMembers, headsMap]
  );

  const showColorLegend = isCompactLayout && legendLines.length > 0;

  // Date and Year both toggle asc → desc → asc on the one button, and carry the
  // arrow for the direction they are in — the same pair In Memoriam leads its bar
  // with. Rendered here rather than in the shared bar because only these two pages
  // offer them.
  const renderDirectionalSortButton = (label, ascKey, descKey, title) => (
    <button
      type="button"
      onClick={() => setSortBy(sortBy === ascKey ? descKey : ascKey)}
      className={`dashboard-detail-sort-btn ${
        sortBy === ascKey || sortBy === descKey ? "active" : ""
      }`}
      title={title}
    >
      {label}
      {sortBy === ascKey ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: "4px" }}
        >
          <path d="M18 15l-6-6-6 6" />
        </svg>
      ) : sortBy === descKey ? (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginLeft: "4px" }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      ) : (
        <span
          style={{
            marginLeft: "4px",
            display: "inline-flex",
            flexDirection: "column",
            lineHeight: 1,
            alignItems: "center",
          }}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block" }}
          >
            <path d="M18 15l-6-6-6 6" />
          </svg>
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ display: "block", marginTop: "-2px" }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      )}
    </button>
  );

  const leadingSortButtons = (
    <>
      <DashboardPeriodPickers
        month={month}
        onMonthChange={setMonth}
        year={year}
        onYearChange={setYear}
      />
      {renderDirectionalSortButton("Date", "date-asc", "date-desc", "Sort by Date")}
      {renderDirectionalSortButton("Year", "year-asc", "year-desc", "Sort by Year")}
    </>
  );

  // Spouse(s) (Living) is the list the page opens on, so — like a default sort —
  // it is grayed out while on screen and lights back up as the way back once
  // Spouse(s) (Widowed/Deceased) takes over.
  const deceasedSpousesFilterButton = (
    <>
    <button
      type="button"
      onClick={() => {
        if (showDeceasedSpouses) setShowDeceasedSpouses(false);
      }}
      className={`dashboard-detail-sort-btn${
        showDeceasedSpouses ? "" : " dashboard-detail-sort-btn--disabled"
      }`}
      disabled={!showDeceasedSpouses}
      aria-disabled={!showDeceasedSpouses}
      title={
        showDeceasedSpouses
          ? "Show only couples with both spouses living"
          : "Already the default view"
      }
    >
      Spouse(s) (Living)
    </button>
    <button
      type="button"
      onClick={() => setShowDeceasedSpouses((on) => !on)}
      className={`dashboard-detail-sort-btn ${showDeceasedSpouses ? "active" : ""}`}
      title="Show only couples who were still married when a spouse passed away"
    >
      Spouse(s) (Widowed/Deceased)
    </button>
    </>
  );

  return (
    <div className={`dashboard-container anniversaries-this-week-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left" ref={headerLeftRef}>
          <DashboardBackButton />
            <h3 className="dashboard-detail-title">{listTitle}</h3>
            <div className="dashboard-detail-count">
              {totalCount}
            </div>
            {/* Up whenever the list has loaded, empty month included — the
                pickers on it are how the reader moves to another month. */}
            {!loading && (
              <DashboardDetailSortFilterBar
                sortBy={sortBy}
                onSortChange={setSortBy}
                showAgeSorts={false}
                showMemberFilters={false}
                leadingSortButtons={leadingSortButtons}
                trailingFilterButtons={deceasedSpousesFilterButton}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading anniversaries...</p>
          </div>
        ) : totalCount === 0 ? (
          <div className="dashboard-detail-empty-state anniversaries-no-data-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : (
          <div className="anniversaries-list dashboard-detail-list" ref={listRef}>
            {sortedMonths.map((group) => (
              <React.Fragment key={group.monthIndex}>
                {/* Month header row */}
                <div className={`dashboard-detail-row dashboard-detail-header-row ${isLargeView ? 'large-view' : ''}`}>
                  <div className={`dashboard-detail-date-col ${isLargeView ? 'large-view' : ''}`} style={{ width: isCompactLayout ? 'auto' : dateColumnWidth }}>
                    <span className="dashboard-detail-month-name dashboard-section-heading">
                      {group.monthName}
                      {/* With All Months picked each heading carries its own count; a single month already shows it at the top. */}
                      {month === ALL_MONTHS && (
                        <span className="dashboard-section-heading-count">{` (${group.rows.length})`}</span>
                      )}
                    </span>
                  </div>
                  {!isCompactLayout && (
                    <>
                      <div className="dashboard-detail-years-col" style={{ width: yearsColumnWidth }}>
                        {/* Empty for alignment */}
                      </div>
                      <div className="dashboard-detail-name-col" style={{ width: nameColumnWidth }}>
                        {/* Empty for alignment */}
                      </div>
                    </>
                  )}
                  {!isCompactLayout && (
                    <div className="dashboard-detail-head-col" style={{ width: headOfLineColumnWidth }}>
                      {/* Empty for alignment */}
                    </div>
                  )}
                </div>
                {group.rows.map((anniversary, index) => (
                  <div key={anniversary?.id || index}>
                    {renderMemberRow(anniversary)}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Pinned under the list whatever is picked — one month, All Months or
            an empty month — so the reader always finds the gems and the legend
            in the same place. Kept out of the list, which scrolls on its own. */}
        <div className="anniversaries-report-footer">
          <p className="anniversaries-gems-note">{ANNIVERSARY_GEMS_NOTE}</p>
          <div className="anniversaries-this-week-page in-law-footer">
            <DashboardMarkerLegend />
            {showColorLegend && (
              <DashboardColorLegend lines={legendLines} />
            )}
          </div>
        </div>
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default AnniversariesThisWeek;
